import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { LoginService } from '@ebw-monorepo/login';
import { safeJsonStringify } from '@ebw-monorepo/microservices-helpers/utils/parser';

interface HttpOptionsInterface {
  headers?:
    | HttpHeaders
    | {
        [header: string]: string | string[];
      };
  observe?: 'body';
  body?: any;
  params?:
    | HttpParams
    | {
        [param: string]: string | string[];
      };
  reportProgress?: boolean;
  responseType?: any;
  withCredentials?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UtilsService {
  /**
   * Constructor
   */
  constructor(private http: HttpClient, private loginService: LoginService) {}

  /**
   * Create HTTP Parameters
   * @param parameters
   * @returns
   */
  private createHttpParameters(parameters?: {
    [param: string]: readonly (string | number | boolean)[] | string | number | boolean;
  }): HttpParams {
    let params = new HttpParams();

    for (const key in parameters) {
      if (parameters[key] != undefined && parameters[key] != null) {
        if (Array.isArray(parameters[key])) {
          params = params.set(key, (parameters[key] as readonly (string | number | boolean)[]).join());
        } else if (
          typeof parameters[key] === 'string' ||
          typeof parameters[key] === 'number' ||
          typeof parameters[key] === 'boolean'
        ) {
          params = params.set(key, parameters[key] as string);
        }
      }
    }
    return params;
  }

  /**
   * Get Authorization Header
   * @returns HttpHeaders
   */
  private getAuthorizationHeader(content_type?: string) {
    let headers = new HttpHeaders({
      Authorization: 'Bearer ' + this.loginService.getToken()
    });

    if (content_type && content_type !== 'FILE') {
      headers = headers.set('Content-Type', content_type);
    } else if (!content_type) {
      headers = headers.set('Content-Type', 'application/x-www-form-urlencoded');
    }

    return { headers };
  }

  /**
   * Function to make a GET request to the backend
   * @param url url to call, eg: this.creaURL('user', 'get')
   * @param parameters Parameters. es: { ids:[1,2,3] }
   * @returns Observable<unknown>
   */
  getRequest(
    url: string,
    parameters?: { [param: string]: readonly (string | number | boolean)[] | string | number | boolean },
    httpOptions?: HttpOptionsInterface
  ) {
    let params = this.createHttpParameters(parameters);

    const opt = { ...httpOptions, ...this.getAuthorizationHeader(), params: params };

    let user_id = sessionStorage.getItem('user_id');

    if (user_id) {
      opt.headers = opt.headers.set('user', user_id);
    }

    return this.http
      .get(url, opt)
      .pipe(
        map((resp: any) => {
          // Error
          if (resp.error && resp.error !== 0) {
            console.log(`GET Request Error`, resp);
          }

          // Returns the response
          return resp;
        })
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  getFile(
    url: string,
    parameters: { [param: string]: readonly (string | number | boolean)[] | string | number | boolean },
    httpOptions?: HttpOptionsInterface
  ) {
    let params = this.createHttpParameters(parameters);

    const opt = { ...httpOptions, ...this.getAuthorizationHeader(), params: params };

    return this.http.get(url, opt).pipe(map((resp: any) => resp));
  }

  /**
   * Function to make a POST request to the backend
   * @param url url to call, es: this.creaURL('soggetti', 'save')
   * @param body request body, es: { "soggetto": {...} }
   * @param serialize Whether or not to serialize the whole request in a string.
   * @param httpOptions HttpOptionsInterface of the request.
   * @returns Observable<unknown>
   */
  postRequest(
    url: string,
    body: any,
    serialize = true,
    httpOptions?: HttpOptionsInterface,
    content_type?: string
  ): Observable<any> {
    const opt = { ...httpOptions, ...this.getAuthorizationHeader(content_type) };

    let user_id = sessionStorage.getItem('user_id');

    if (user_id) {
      opt.headers = opt.headers.set('user', user_id);
    }

    const params = serialize ? new HttpParams().set('serialized', safeJsonStringify(body) ?? '').toString() : body;

    return this.http
      .post(url, params, opt)
      .pipe(
        map((resp: any) => {
          // Returns the response
          return resp;
        })
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  /**
   * Function to make a PUT request to the backend
   * @param url url to call, es: this.creaURL('soggetti', 'save')
   * @param body request body,  es: { "soggetto": {...} }
   * @param httpOptions httpOtions of the request.
   * @returns Observable<unknown>
   */
  putRequest(
    url: string,
    body: any,
    serialize = true,
    httpOptions?: HttpOptionsInterface,
    content_type?: string
  ): Observable<unknown> {
    const opt = { ...httpOptions, ...this.getAuthorizationHeader(content_type) };

    const params = serialize ? new HttpParams().set('serialized', safeJsonStringify(body) ?? '') : body;

    return this.http
      .put(url, params, opt)
      .pipe(
        map((resp: any) => {
          // Error
          if (resp.error && resp.error !== 0) {
            console.log(`Errore postRequest`, resp);
          }

          // Returns the response
          return resp;
        })
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  /**
   * Function to make a PUT request to the backend
   * @param url url to call, es: this.creaURL('soggetti', 'save')
   * @param body request body,  es: { "soggetto": {...} }
   * @param httpOptions httpOtions of the request.
   * @returns Observable<unknown>
   */
  patchRequest(
    url: string,
    body: any,
    serialize = true,
    httpOptions?: HttpOptionsInterface,
    content_type?: string
  ): Observable<unknown> {
    const opt = { ...httpOptions, ...this.getAuthorizationHeader(content_type) };
    const params = serialize ? new HttpParams().set('serialized', safeJsonStringify(body) ?? '') : body;

    return this.http
      .patch(url, params, opt)
      .pipe(
        map((resp: any) => {
          // Error
          if (resp.error && resp.error !== 0) {
            console.log(`Errore postRequest`, resp);
          }

          // Returns the response
          return resp;
        })
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  /**
   * Function to make a DELETE request to the backend
   * @param url
   * @param parameters
   * @param httpOptions
   * @returns
   */
  deleteRequest(
    url: string,
    body: any,
    serialize = true,
    httpOptions?: HttpOptionsInterface,
    content_type?: string
  ): Observable<any> {
    let PARAMS = serialize ? new HttpParams().set('raw', safeJsonStringify(body) ?? '') : body;
    const opt = { ...httpOptions, ...this.getAuthorizationHeader(content_type), body: PARAMS };

    let user_id = sessionStorage.getItem('user_id');

    if (user_id) {
      opt.headers = opt.headers.set('user', user_id);
    }

    return this.http.delete(url, opt).pipe(catchError((error) => throwError(() => new Error(error))));
  }

  /**
   * Function to make a GET request to the backend
   * @param url url to call, eg: this.creaURL('user', 'get')
   * @param parameters Parameters. es: { ids:[1,2,3] }
   * @returns Observable<unknown>
   */
  getRequestNoAuth(
    url: string,
    parameters: { [param: string]: readonly (string | number | boolean)[] | string | number | boolean },
    httpOptions?: HttpOptionsInterface
  ): Observable<any> {
    let params = this.createHttpParameters(parameters);

    const opt = { ...httpOptions, params: params };

    return this.http
      .get(url, opt)
      .pipe(
        map((resp: any) => {
          // Error
          if (resp.error && resp.error !== 0) {
            console.log(`GET Request Error`, resp);
          }

          // Returns the response
          return resp;
        })
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  /**
   * Function to make a POST request to the backend
   * @param url url to call, es: this.creaURL('soggetti', 'save')
   * @param body request body, es: { "soggetto": {...} }
   * @param serialize Whether or not to serialize the whole request in a string.
   * @param httpOptions HttpOptionsInterface of the request.
   * @returns Observable<unknown>
   */
  postRequestNoAuth(url: string, body: any, serialize = true, httpOptions?: HttpOptionsInterface): Observable<unknown> {
    const opt = { ...httpOptions };

    const params = serialize ? new HttpParams().set('serialized', safeJsonStringify(body) ?? '').toString() : body;

    return this.http
      .post(url, params, opt)
      .pipe(
        map((resp: any) => {
          // Error
          if (resp.error && resp.error !== 0) {
            console.log(`Errore postRequest`, resp);
          }

          // Returns the response
          return resp;
        })
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  /**
   * Function to make a PUT request to the backend
   * @param url url to call, es: this.creaURL('soggetti', 'save')
   * @param body request body,  es: { "soggetto": {...} }
   * @param httpOptions httpOtions of the request.
   * @returns Observable<unknown>
   */
  putRequestNoAuth(url: string, body: any, httpOptions?: HttpOptionsInterface): Observable<unknown> {
    const params = new HttpParams().set('serialized', safeJsonStringify(body) ?? '');

    const opt = { ...httpOptions };

    return this.http
      .put(url, params.toString(), opt)
      .pipe(
        map((resp: any) => {
          // Error
          if (resp.error && resp.error !== 0) {
            console.log(`Errore postRequest`, resp);
          }

          // Returns the response
          return resp;
        })
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  /**
   * Function to make a DELETE request to the backend
   * @param url
   * @param parameters
   * @param httpOptions
   * @returns
   */
  deleteRequestNoAuth(url: string, body: any, httpOptions?: HttpOptionsInterface): Observable<unknown> {
    let PARAMS = new HttpParams().set('raw', safeJsonStringify(body) ?? '');

    const opt = { ...httpOptions, body: PARAMS };

    return this.http
      .delete(url, opt)
      .pipe(
        map((resp: any) => {
          // Error
          if (resp.error && resp.error !== 0) {
            console.log(`Errore postRequest`, resp);
          }

          // Returns the response
          return resp;
        })
      )
      .pipe(
        catchError((error) => {
          return throwError(error);
        })
      );
  }
}
